import { useState, useCallback } from 'react';
import { Navigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import Papa from 'papaparse';
import { ArrowLeft, ArrowRight, Lock, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Layout } from '@/components/layout/Layout';
import { useAuthStore } from '@/stores/authStore';
import { useQRCodes } from '@/hooks/useQRCodes';
import { useSubscription } from '@/hooks/useSubscription';
import { 
  useBatchJobs, 
  BatchRowData, 
  ValidationError, 
  validateBatchRow,
  batchLimits 
} from '@/hooks/useBatchJobs';
import { QRDesignSettings, QRType } from '@/stores/qrStore';
import { designPresets } from '@/lib/design-constants';
import { generateQRContent } from '@/lib/qr-utils';
import { BatchWizardSteps } from '@/components/batch/BatchWizardSteps';
import { BatchUploadStep } from '@/components/batch/BatchUploadStep';
import { BatchPreviewStep } from '@/components/batch/BatchPreviewStep';
import { BatchDesignStep } from '@/components/batch/BatchDesignStep';
import { BatchGenerateStep } from '@/components/batch/BatchGenerateStep';
import { BatchResultsStep } from '@/components/batch/BatchResultsStep';
import { toast } from 'sonner';

const WIZARD_STEPS = [
  { label: 'Upload', description: 'CSV file' },
  { label: 'Preview', description: 'Validate data' },
  { label: 'Design', description: 'Apply style' },
  { label: 'Generate', description: 'Create QRs' },
  { label: 'Download', description: 'Get results' },
];

export default function BatchCreate() {
  const { user, isLoading: authLoading } = useAuthStore();
  const { saveQRCode } = useQRCodes();
  const { createBatchJob, updateBatchJob } = useBatchJobs();
  const { plan, isLoading: subscriptionLoading } = useSubscription();
  
  // Wizard state
  const [currentStep, setCurrentStep] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  
  // Data state
  const [csvData, setCsvData] = useState<BatchRowData[]>([]);
  const [csvColumns, setCsvColumns] = useState<string[]>([]);
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);
  
  // Design state
  const [designMode, setDesignMode] = useState<'single' | 'preset' | 'per-row'>('preset');
  const [selectedPreset, setSelectedPreset] = useState('modern');
  const [customDesign, setCustomDesign] = useState<Partial<QRDesignSettings>>({
    dotsColor: '#1a1a2e',
    backgroundColor: '#ffffff',
  });
  
  // Generation state
  const [progress, setProgress] = useState({ processed: 0, success: 0, failed: 0 });
  const [results, setResults] = useState<{ index: number; success: boolean; error?: string; qrId?: string }[]>([]);
  const [batchJobId, setBatchJobId] = useState<string | null>(null);

  // Get plan-based limits
  const userPlan = plan || 'free';
  const maxRows = batchLimits[userPlan as keyof typeof batchLimits] ?? 0;
  const hasBatchAccess = maxRows !== 0;

  // Get final design settings
  const getDesignSettings = useCallback((): Partial<QRDesignSettings> => {
    if (designMode === 'preset') {
      const preset = designPresets.find(p => p.id === selectedPreset);
      return {
        dotsColor: preset?.dotsColor || '#1a1a2e',
        backgroundColor: preset?.backgroundColor || '#ffffff',
        dotsType: preset?.dotsType || 'rounded',
      };
    }
    return customDesign;
  }, [designMode, selectedPreset, customDesign]);

  // Handle file upload
  const handleFileSelect = useCallback(async (file: File) => {
    if (!file.name.endsWith('.csv')) {
      toast.error('Please upload a CSV file');
      return;
    }
    
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size must be less than 10 MB');
      return;
    }
    
    setIsUploading(true);
    
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        const data = results.data as BatchRowData[];
        
        if (data.length === 0) {
          toast.error('CSV file is empty');
          setIsUploading(false);
          return;
        }
        
        if (data.length > maxRows) {
          toast.error(`Maximum ${maxRows} rows allowed on your plan`);
          data.splice(maxRows);
        }
        
        // Get columns from first row
        const columns = Object.keys(data[0]).filter(col => col.trim() !== '');
        setCsvColumns(columns);
        setCsvData(data);
        
        // Validate all rows
        const errors: ValidationError[] = [];
        data.forEach((row, index) => {
          errors.push(...validateBatchRow(row, index));
        });
        setValidationErrors(errors);
        
        setIsUploading(false);
        setCurrentStep(1);
        toast.success(`Loaded ${data.length} rows from CSV`);
      },
      error: (error) => {
        toast.error('Failed to parse CSV: ' + error.message);
        setIsUploading(false);
      },
    });
  }, [maxRows]);

  // Handle data change in preview
  const handleDataChange = useCallback((newData: BatchRowData[]) => {
    setCsvData(newData);
    
    // Re-validate
    const errors: ValidationError[] = [];
    newData.forEach((row, index) => {
      errors.push(...validateBatchRow(row, index));
    });
    setValidationErrors(errors);
  }, []);

  // Generate QR codes
  const handleGenerate = useCallback(async () => {
    if (validationErrors.length > 0) {
      toast.error('Please fix all validation errors first');
      return;
    }
    
    setIsGenerating(true);
    setProgress({ processed: 0, success: 0, failed: 0 });
    setResults([]);
    
    const designSettings = getDesignSettings();
    
    // Create batch job
    const batchJob = await createBatchJob.mutateAsync({
      totalCount: csvData.length,
      designSettings: designSettings as Record<string, unknown>,
    });
    setBatchJobId(batchJob.id);
    
    // Process in batches
    const batchSize = 10;
    const newResults: typeof results = [];
    
    for (let i = 0; i < csvData.length; i += batchSize) {
      const batch = csvData.slice(i, Math.min(i + batchSize, csvData.length));
      
      await Promise.all(batch.map(async (row, batchIndex) => {
        const index = i + batchIndex;
        
        try {
          // Validate the row has required data
          const rowErrors = validateBatchRow(row, index);
          if (rowErrors.length > 0) {
            throw new Error(rowErrors[0].message);
          }
          
          // Generate content
          const qrType = (row.type || 'url') as QRType;
          const content = generateQRContent(qrType, row);
          
          // Save to database
          await saveQRCode.mutateAsync({
            name: row.name || `Batch QR ${index + 1}`,
            type: qrType,
            contentData: row,
            designSettings: {
              ...designSettings,
              dotsType: (designSettings.dotsType || 'rounded') as any,
              cornersSquareType: 'extra-rounded',
              cornersDotType: 'dot',
              gradient: { enabled: false, type: 'linear', color1: '#ffffff', color2: '#f0f0f0', rotation: 0 },
              logo: { enabled: false, url: '', size: 25, transparentBackground: true },
              frame: { style: 'none', text: 'SCAN ME', color: '#1a1a2e', thickness: 20, textPosition: 'bottom' },
              errorCorrectionLevel: 'M',
              width: 300,
              height: 300,
              margin: 10,
              downloadFormat: 'png',
              resolution: 1000,
              backgroundTransparent: false,
            } as QRDesignSettings,
            isDynamic: false,
          });
          
          newResults.push({ index, success: true });
          setProgress(prev => ({ 
            ...prev, 
            processed: prev.processed + 1,
            success: prev.success + 1 
          }));
        } catch (error: any) {
          newResults.push({ index, success: false, error: error.message });
          setProgress(prev => ({ 
            ...prev, 
            processed: prev.processed + 1,
            failed: prev.failed + 1 
          }));
        }
      }));
      
      setResults([...newResults]);
    }
    
    // Update batch job
    const successCount = newResults.filter(r => r.success).length;
    const failedCount = newResults.filter(r => !r.success).length;
    
    await updateBatchJob.mutateAsync({
      id: batchJob.id,
      updates: {
        status: failedCount === csvData.length ? 'failed' : 'completed',
        processed_count: csvData.length,
        success_count: successCount,
        failed_count: failedCount,
        completed_at: new Date().toISOString(),
        error_log: newResults.filter(r => !r.success).map(r => ({
          row: r.index,
          error: r.error || 'Unknown error',
        })),
      },
    });
    
    setIsGenerating(false);
    setCurrentStep(4);
    
    if (failedCount === 0) {
      toast.success(`All ${successCount} QR codes generated successfully!`);
    } else {
      toast.warning(`Generated ${successCount} QR codes. ${failedCount} failed.`);
    }
  }, [csvData, validationErrors, getDesignSettings, createBatchJob, updateBatchJob, saveQRCode]);

  // Cancel generation
  const handleCancel = useCallback(async () => {
    setIsGenerating(false);
    
    if (batchJobId) {
      await updateBatchJob.mutateAsync({
        id: batchJobId,
        updates: { status: 'cancelled' },
      });
    }
    
    toast.info('Batch generation cancelled');
  }, [batchJobId, updateBatchJob]);

  // Save to account (already done during generation)
  const handleSaveToAccount = async () => {
    // QR codes are already saved during generation
    return Promise.resolve();
  };

  // Start over
  const handleStartOver = () => {
    setCurrentStep(0);
    setCsvData([]);
    setCsvColumns([]);
    setValidationErrors([]);
    setResults([]);
    setProgress({ processed: 0, success: 0, failed: 0 });
    setBatchJobId(null);
  };

  // Navigation
  const canGoNext = () => {
    switch (currentStep) {
      case 0:
        return csvData.length > 0;
      case 1:
        return validationErrors.length === 0;
      case 2:
        return true;
      case 3:
        return !isGenerating;
      default:
        return false;
    }
  };

  const handleNext = () => {
    if (currentStep < WIZARD_STEPS.length - 1 && canGoNext()) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 0 && !isGenerating) {
      setCurrentStep(currentStep - 1);
    }
  };

  if (authLoading || subscriptionLoading) {
    return (
      <Layout>
        <div className="min-h-[80vh] flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </div>
      </Layout>
    );
  }

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  // Show upgrade prompt for free users
  if (!hasBatchAccess) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-8 max-w-2xl">
          <Link to="/dashboard">
            <Button variant="ghost" className="mb-6">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Dashboard
            </Button>
          </Link>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center py-12"
          >
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-primary/10 flex items-center justify-center">
              <Lock className="h-10 w-10 text-primary" />
            </div>
            <h2 className="text-2xl font-bold mb-3">Batch Creation is a Premium Feature</h2>
            <p className="text-muted-foreground max-w-md mx-auto mb-6">
              Upgrade to Starter or higher to create multiple QR codes at once using CSV uploads. Save time by generating up to 500 QR codes in a single batch.
            </p>
            <div className="flex gap-3 justify-center">
              <Link to="/pricing">
                <Button className="gradient-hero">
                  <Sparkles className="h-4 w-4 mr-2" />
                  Upgrade Now
                </Button>
              </Link>
              <Link to="/create">
                <Button variant="outline">
                  Create Single QR
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container mx-auto px-4 py-8 max-w-4xl overflow-x-hidden">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center gap-4 mb-8"
        >
          <Link to="/dashboard">
            <Button variant="ghost" size="icon">
              <ArrowLeft className="h-4 w-4" />
            </Button>
          </Link>
          <div>
            <h1 className="text-2xl font-bold">Batch Create QR Codes</h1>
            <p className="text-sm text-muted-foreground">
              Create up to {maxRows === -1 ? 'unlimited' : maxRows} QR codes at once
            </p>
          </div>
        </motion.div>

        {/* Wizard Steps */}
        <BatchWizardSteps currentStep={currentStep} steps={WIZARD_STEPS} />

        {/* Step Content */}
        <div className="mb-8">
          <AnimatePresence mode="wait">
            {currentStep === 0 && (
              <BatchUploadStep
                key="upload"
                onFileSelect={handleFileSelect}
                maxRows={maxRows}
                isUploading={isUploading}
              />
            )}
            
            {currentStep === 1 && (
              <BatchPreviewStep
                key="preview"
                data={csvData}
                errors={validationErrors}
                columns={csvColumns}
                onDataChange={handleDataChange}
                onReupload={() => setCurrentStep(0)}
              />
            )}
            
            {currentStep === 2 && (
              <BatchDesignStep
                key="design"
                designSettings={customDesign}
                designMode={designMode}
                selectedPreset={selectedPreset}
                onDesignChange={setCustomDesign}
                onModeChange={setDesignMode}
                onPresetChange={setSelectedPreset}
                previewCount={csvData.length}
              />
            )}
            
            {currentStep === 3 && (
              <BatchGenerateStep
                key="generate"
                data={csvData}
                designSettings={getDesignSettings()}
                isGenerating={isGenerating}
                progress={progress}
                onGenerate={handleGenerate}
                onCancel={handleCancel}
              />
            )}
            
            {currentStep === 4 && (
              <BatchResultsStep
                key="results"
                data={csvData}
                results={results}
                designSettings={getDesignSettings()}
                onSaveToAccount={handleSaveToAccount}
                onStartOver={handleStartOver}
              />
            )}
          </AnimatePresence>
        </div>

        {/* Navigation */}
        {currentStep < 4 && (
          <div className="flex items-center justify-between">
            <Button
              variant="outline"
              onClick={handleBack}
              disabled={currentStep === 0 || isGenerating}
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
            
            {currentStep < 3 && (
              <Button
                onClick={handleNext}
                disabled={!canGoNext()}
              >
                Next
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
}
